import { useTranslation } from "react-i18next";
import { FoundingStatusBadge } from "./FoundingStatusBadge";
import { currentPercent, deriveFoundingStatus, type FoundingReportRow } from "./types";

interface FoundingPropertiesTableProps {
  readonly rows: readonly FoundingReportRow[];
  readonly selected: ReadonlySet<string>;
  /** When false (non-admin viewer), the select column is hidden entirely. */
  readonly selectable?: boolean;
  /** True while a background refetch is in flight — dims the table. */
  readonly busy?: boolean;
  readonly onToggle: (propertyId: string) => void;
  readonly onToggleAll: (checked: boolean) => void;
}

const TH_CLASS =
  "px-3 py-2 text-left font-sans text-xs font-medium uppercase tracking-wide text-muted-foreground";
const TD_CLASS = "px-3 py-3 align-top font-sans text-sm";

function formatDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(date);
}

export function FoundingPropertiesTable({
  rows,
  selected,
  selectable = false,
  busy = false,
  onToggle,
  onToggleAll,
}: FoundingPropertiesTableProps) {
  const { t } = useTranslation();
  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.propertyId));

  return (
    <div
      aria-busy={busy}
      className={
        busy
          ? "overflow-x-auto rounded-lg border border-border opacity-70 transition-opacity"
          : "overflow-x-auto rounded-lg border border-border"
      }
    >
      <table data-testid="founding-properties-table" className="w-full border-collapse">
        <thead className="border-b border-border bg-muted/30">
          <tr>
            {selectable && (
              <th className={TH_CLASS}>
                <input
                  type="checkbox"
                  aria-label={t("founding.table.selectAll")}
                  data-testid="founding-select-all"
                  checked={allSelected}
                  onChange={(e) => onToggleAll(e.target.checked)}
                />
              </th>
            )}
            <th className={TH_CLASS}>{t("founding.table.property")}</th>
            <th className={TH_CLASS}>{t("founding.table.status")}</th>
            <th className={TH_CLASS}>{t("founding.table.discount")}</th>
            <th className={TH_CLASS}>{t("founding.table.tier")}</th>
            <th className={TH_CLASS}>{t("founding.table.market")}</th>
            <th className={TH_CLASS}>{t("founding.table.created")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const pct = currentPercent(r.founding);
            return (
              <tr
                key={r.propertyId}
                data-testid={`founding-row-${r.propertyId}`}
                className="border-b border-border last:border-0 hover:bg-muted/20"
              >
                {selectable && (
                  <td className={TD_CLASS}>
                    <input
                      type="checkbox"
                      aria-label={t("founding.table.selectRow", {
                        name: r.propertyName,
                      })}
                      data-testid={`founding-select-${r.propertyId}`}
                      checked={selected.has(r.propertyId)}
                      onChange={() => onToggle(r.propertyId)}
                    />
                  </td>
                )}
                <td className={TD_CLASS}>
                  <p className="font-medium">{r.propertyName}</p>
                  <p className="text-xs text-muted-foreground">{r.ownerName}</p>
                </td>
                <td className={TD_CLASS}>
                  <FoundingStatusBadge
                    status={deriveFoundingStatus(r.founding)}
                    testId={`founding-status-${r.propertyId}`}
                  />
                </td>
                <td className={TD_CLASS}>{pct === null ? "—" : `${pct}%`}</td>
                <td className={TD_CLASS}>{r.currentTier ?? "—"}</td>
                <td className={TD_CLASS}>{r.country}</td>
                <td className={TD_CLASS}>{formatDate(r.createdAt)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
